import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, POSTS, HTTP_METHOD } from '../constants';
import { IPost } from '../../../types/interfaces/post';
import { IReaction } from '../../../types/interfaces/reaction';
import { IComment } from '../../../types/interfaces/comment';
import { INewFile } from '../../../types/interfaces/new-file';

export const postsApi = createApi({
  reducerPath: 'postsApi',
  tagTypes: [
    POSTS.POSTS,
    POSTS.NEWS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserPostsApi: builder.query<
      IPost[],
      { timestamp?: number; userId: string; pageSize: number }
    >({
      keepUnusedDataFor: 3600,
      query(params) {
        let url = `${endpoints.Post.USER}/${params.userId}`;

        if (params.timestamp || params.pageSize) {
          url +=
            '?' +
            getUrlParamsString({
              timestamp: params.timestamp,
              pageSize: params.pageSize,
            });
        }

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      providesTags: (result) => getListProvidesTags(result, POSTS.POSTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getNewsApi: builder.query<
      IPost[],
      { timestamp?: number; pageSize: number } | void
    >({
      keepUnusedDataFor: 60,
      query(params) {
        let url: string = endpoints.Post.NEWS;

        if (params) {
          url += '?' + getUrlParamsString(params);
        }

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      providesTags: (result) => getListProvidesTags(result, POSTS.NEWS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAllCommunityPostsApi: builder.query<IPost[], { communityId: string }>({
      keepUnusedDataFor: 3600,
      query({ communityId }) {
        return {
          url: `${endpoints.Post.COMMUNITY}/${communityId}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, POSTS.POSTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutPostApi: builder.mutation<
      void,
      {
        id?: string;
        body: {
          post?: IPost;
          filesToUpload?: INewFile[];
          fileIdsToDelete?: string[];
        };
      }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Post.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: POSTS.POSTS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: POSTS.POSTS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Post'),
    }),
    deletePostApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Post.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: POSTS.POSTS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Post'),
    }),
    postPutPostCommentApi: builder.mutation<
      void,
      {
        id?: string;
        postId: string;
        body: {
          comment?: IComment;
          filesToUpload?: INewFile[];
          fileIdsToDelete?: string[];
        };
      }
    >({
      query({ postId, id, body }) {
        return {
          url:
            endpoints.Post.COMMENT.replace('{0}', postId) +
            (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: POSTS.POSTS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: POSTS.POSTS,
              id: params.postId,
            },
            {
              type: POSTS.NEWS,
              id: params.postId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Post comment'),
    }),
    deletePostCommentApi: builder.mutation<
      void,
      { id: string; postId: string }
    >({
      query({ postId, id }) {
        return {
          url: `${endpoints.Post.COMMENT.replace('{0}', postId)}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: POSTS.POSTS,
              id: params.postId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Post comment'),
    }),
    addPostReactionApi: builder.mutation<
      void,
      { id: string; body?: IReaction }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Post.REACTION.replace('{0}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    addPostCommentReactionApi: builder.mutation<
      void,
      { id: string; postId: string; body?: IReaction }
    >({
      query({ id, postId, body }) {
        return {
          url: endpoints.Post.COMMENT_REACTION.replace('{0}', postId).replace(
            '{1}',
            id,
          ),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetUserPostsApiQuery: useGetUserPosts,
  useGetNewsApiQuery: useGetNews,
  useLazyGetNewsApiQuery: useLazyGetNews,
  useLazyGetUserPostsApiQuery: useLazyGetUserPosts,
  useGetAllCommunityPostsApiQuery: useGetAllCommunityPosts,
  usePostPutPostApiMutation: usePostPutPost,
  useDeletePostApiMutation: useDeletePost,
  usePostPutPostCommentApiMutation: usePostPutPostComment,
  useDeletePostCommentApiMutation: useDeletePostComment,
  useAddPostReactionApiMutation: useAddPostReaction,
  useAddPostCommentReactionApiMutation: useAddPostCommentReaction,
} = postsApi;
